import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    TextField,
    Button,
    Typography,
    Drawer,
    List,
    ListItem,
    ListItemText,
    Divider,
    CircularProgress,
    Alert,
    Collapse,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
} from '@mui/material';
import { Edit, Save, Cancel, Add, Delete, ExpandMore, ExpandLess, EditNote, AutoAwesome } from '@mui/icons-material';
import LocaleService from '../../services/LocaleService';
import { useStores } from '../../hooks/use-stores';
import services from '../../services';

const LanguagesEditor = () => {
    const { superStore } = useStores();
    const [namespace, setNamespace] = useState('common');
    const [language, setLanguage] = useState('en');
    const [localeData, setLocaleData] = useState({});
    const [enLocaleData, setEnLocaleData] = useState({});
    const [editingKey, setEditingKey] = useState(null);
    const [editValue, setEditValue] = useState('');
    const [changes, setChanges] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [addingNew, setAddingNew] = useState(false);
    const [newKey, setNewKey] = useState('');
    const [newValue, setNewValue] = useState('');
    const [existingExpanded, setExistingExpanded] = useState(true);
    const [missingExpanded, setMissingExpanded] = useState(true);
    const [commitDialogOpen, setCommitDialogOpen] = useState(false);
    const [commitMessage, setCommitMessage] = useState('');
    const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
    const [bulkEditKey, setBulkEditKey] = useState('');
    const [bulkEditValues, setBulkEditValues] = useState({});
    const [allLanguagesData, setAllLanguagesData] = useState({});
    const [loadingBulkData, setLoadingBulkData] = useState(false);
    const [languageCache, setLanguageCache] = useState({});
    const [loadingBulkKey, setLoadingBulkKey] = useState(null);
    const [translatingAI, setTranslatingAI] = useState(false);

    const namespaces = LocaleService.getAvailableNamespaces();
    const languages = superStore.languages || [];

    useEffect(() => {
        loadLocaleData();
    }, [namespace, language]);

    useEffect(() => {
        // Load languages from superStore if not already loaded
        if (!superStore.languages || superStore.languages.length === 0) {
            superStore.loadLanguages();
        }
    }, []);

    const loadLocaleData = async () => {
        setLoading(true);
        setError(null);
        try {
            // Load target language data - treat 403/404 as empty file
            let data = {};
            try {
                data = await LocaleService.fetchLocale(language, namespace);
            } catch (e) {
                // If file doesn't exist or access denied, treat as empty
                if (e.response && (e.response.status === 403 || e.response.status === 404)) {
                    console.log(`Locale file ${language}/${namespace} not found, treating as empty`);
                    data = {};
                } else {
                    throw e;
                }
            }
            setLocaleData(data);

            // Always load English data for comparison
            if (language !== 'en') {
                let enData = {};
                try {
                    enData = await LocaleService.fetchLocale('en', namespace);
                } catch (e) {
                    // If English file doesn't exist, treat as empty
                    if (e.response && (e.response.status === 403 || e.response.status === 404)) {
                        console.log(`English locale file ${namespace} not found, treating as empty`);
                        enData = {};
                    } else {
                        throw e;
                    }
                }
                setEnLocaleData(enData);
            } else {
                setEnLocaleData(data);
            }
        } catch (e) {
            setError(`Failed to load locale data: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (key, value) => {
        setEditingKey(key);
        setEditValue(value || '');
    };

    const handleSave = (key) => {
        const changeKey = `${namespace}:${language}`;
        const updatedChanges = { ...changes };
        
        if (!updatedChanges[changeKey]) {
            updatedChanges[changeKey] = {};
        }
        
        updatedChanges[changeKey][key] = editValue;
        setChanges(updatedChanges);

        const updatedLocaleData = { ...localeData, [key]: editValue };
        setLocaleData(updatedLocaleData);

        setEditingKey(null);
        setEditValue('');
    };

    const handleCancel = () => {
        setEditingKey(null);
        setEditValue('');
    };

    const handleAddNew = () => {
        if (!newKey.trim()) {
            alert('Please enter a key');
            return;
        }

        const changeKey = `${namespace}:${language}`;
        const updatedChanges = { ...changes };
        
        if (!updatedChanges[changeKey]) {
            updatedChanges[changeKey] = {};
        }
        
        updatedChanges[changeKey][newKey] = newValue;
        setChanges(updatedChanges);

        const updatedLocaleData = { ...localeData, [newKey]: newValue };
        setLocaleData(updatedLocaleData);

        setAddingNew(false);
        setNewKey('');
        setNewValue('');
    };

    const handleDeleteChange = (changeKey, key) => {
        const updatedChanges = { ...changes };
        delete updatedChanges[changeKey][key];
        
        if (Object.keys(updatedChanges[changeKey]).length === 0) {
            delete updatedChanges[changeKey];
        }
        
        setChanges(updatedChanges);
    };

    const handleSaveAll = () => {
        // Open dialog to ask for commit message
        setCommitDialogOpen(true);
    };

    const handleCommitSave = async () => {
        if (!commitMessage.trim()) {
            setError('Commit message is required');
            return;
        }

        setCommitDialogOpen(false);
        setLoading(true);
        setError(null);
        setSuccess(false);
        
        try {
            // Transform changes to the format expected by the server
            const transformedChanges = {};
            
            Object.keys(changes).forEach(key => {
                const [ns, lang] = key.split(':');
                if (!transformedChanges[ns]) {
                    transformedChanges[ns] = {};
                }
                if (!transformedChanges[ns][lang]) {
                    transformedChanges[ns][lang] = {};
                }
                transformedChanges[ns][lang] = changes[key];
            });

            // Send with commit message
            await LocaleService.saveLocaleChanges({
                message: commitMessage,
                data: transformedChanges
            });
            
            setSuccess(true);
            setChanges({});
            setCommitMessage('');
            
            // Reload data
            await loadLocaleData();
        } catch (e) {
            setError(`Failed to save changes: ${e.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleCommitCancel = () => {
        setCommitDialogOpen(false);
        setCommitMessage('');
    };

    const handleBulkEdit = async (key) => {
        setBulkEditKey(key);
        setLoadingBulkKey(key);
        
        try {
            // Don't use cache if there are unsaved changes
            const hasUnsavedChanges = Object.keys(changes).some(
                changeKey => changes[changeKey][key] !== undefined
            );
            
            // Check cache first (only if no unsaved changes)
            const cacheKey = `${namespace}:${key}`;
            if (!hasUnsavedChanges && languageCache[cacheKey]) {
                setAllLanguagesData(languageCache[cacheKey]);
                setBulkEditValues(languageCache[cacheKey]);
                setBulkEditDialogOpen(true);
                setLoadingBulkKey(null);
                return;
            }
            
            setLoadingBulkData(true);
            
            // Load all language data for this key
            const allData = {};
            const loadPromises = languages.map(async (lang) => {
                try {
                    const data = await LocaleService.fetchLocale(lang.code, namespace);
                    allData[lang.code] = data[key] || '';
                } catch (e) {
                    // If file doesn't exist, use empty string
                    allData[lang.code] = '';
                }
            });
            
            await Promise.all(loadPromises);
            
            // Now merge with unsaved changes
            languages.forEach((lang) => {
                const changeKey = `${namespace}:${lang.code}`;
                if (changes[changeKey] && changes[changeKey][key] !== undefined) {
                    allData[lang.code] = changes[changeKey][key];
                }
            });
            
            // Only cache if there are no unsaved changes
            if (!hasUnsavedChanges) {
                setLanguageCache(prev => ({
                    ...prev,
                    [cacheKey]: allData
                }));
            }
            
            setAllLanguagesData(allData);
            setBulkEditValues(allData);
        } catch (e) {
            console.error('Error loading bulk edit data:', e);
            setError('Failed to load language data for bulk edit');
        } finally {
            setLoadingBulkData(false);
            setLoadingBulkKey(null);
            setBulkEditDialogOpen(true);
        }
    };

    const handleBulkEditApply = () => {
        const updatedChanges = { ...changes };
        
        // Add all changed values to pending changes, grouped by language
        Object.keys(bulkEditValues).forEach(langCode => {
            const newValue = bulkEditValues[langCode];
            const originalValue = allLanguagesData[langCode] || '';
            
            // Only add if value has changed
            if (newValue !== originalValue) {
                const changeKey = `${namespace}:${langCode}`;
                
                if (!updatedChanges[changeKey]) {
                    updatedChanges[changeKey] = {};
                }
                
                updatedChanges[changeKey][bulkEditKey] = newValue;
            }
        });
        
        setChanges(updatedChanges);
        
        // Update local data for current language
        if (bulkEditValues[language] !== undefined) {
            const updatedLocaleData = { ...localeData, [bulkEditKey]: bulkEditValues[language] };
            setLocaleData(updatedLocaleData);
        }
        
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
        setAllLanguagesData({});
    };

    const handleBulkEditCancel = () => {
        setBulkEditDialogOpen(false);
        setBulkEditKey('');
        setBulkEditValues({});
        setAllLanguagesData({});
    };

    const handleFillWithAI = async () => {
        setTranslatingAI(true);
        setError(null);

        try {
            // Find empty languages
            const emptyLanguages = languages
                .map(lang => lang.code)
                .filter(code => !bulkEditValues[code] || bulkEditValues[code].trim() === '');

            if (emptyLanguages.length === 0) {
                setError('No empty fields to translate');
                setTranslatingAI(false);
                return;
            }

            // Find first non-empty value (prefer English, otherwise first non-empty)
            let currentValue = null;
            if (bulkEditValues['en'] && bulkEditValues['en'].trim()) {
                currentValue = { lang: 'en', value: bulkEditValues['en'] };
            } else {
                for (const lang of languages) {
                    if (bulkEditValues[lang.code] && bulkEditValues[lang.code].trim()) {
                        currentValue = { lang: lang.code, value: bulkEditValues[lang.code] };
                        break;
                    }
                }
            }

            if (!currentValue) {
                setError('No source text found for translation');
                setTranslatingAI(false);
                return;
            }

            // Call translation API
            const translations = await LocaleService.translateText(emptyLanguages, currentValue);

            // Update bulk edit values with translations
            setBulkEditValues(prev => ({
                ...prev,
                ...translations
            }));

        } catch (e) {
            setError(`Failed to translate: ${e.message || 'Unknown error'}`);
        } finally {
            setTranslatingAI(false);
        }
    };

    const areAllFieldsEmpty = () => {
        return languages.every(lang => 
            !bulkEditValues[lang.code] || bulkEditValues[lang.code].trim() === ''
        );
    };

    const getMissingKeys = () => {
        if (language === 'en') return [];
        
        const enKeys = Object.keys(enLocaleData);
        const targetKeys = Object.keys(localeData);
        
        return enKeys.filter(key => !targetKeys.includes(key));
    };

    const missingKeys = getMissingKeys();
    const existingKeys = Object.keys(localeData).sort();
    const hasChanges = Object.keys(changes).length > 0;
    
    // Show bottom button only if at least one section is expanded
    const showBottomButton = existingExpanded || (missingKeys.length > 0 && missingExpanded);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            {/* Top Bar - Full Width */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
                <Box sx={{ p: 3, pb: 2 }}>
                    <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                        Languages Editor
                    </Typography>

                    {/* Selectors */}
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Namespace</InputLabel>
                                <Select
                                    value={namespace}
                                    label="Namespace"
                                    onChange={(e) => setNamespace(e.target.value)}
                                >
                                    {namespaces.map(ns => (
                                        <MenuItem key={ns} value={ns}>{ns}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Language</InputLabel>
                                <Select
                                    value={language}
                                    label="Language"
                                    onChange={(e) => setLanguage(e.target.value)}
                                >
                                    {languages.map(lang => (
                                        <MenuItem key={lang.code} value={lang.code}>
                                            {lang.name} ({lang.code})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                </Box>
            </Box>

            {/* Content Area with Sidebar */}
            <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
                {/* Main Content */}
                <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>

                    {/* Alerts */}
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}
                    {success && (
                        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
                            Changes saved successfully!
                        </Alert>
                    )}

                    {/* Loading */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!loading && (
                        <>
                            {/* Add New Button - Top */}
                            {addingNew ? (
                                <Paper sx={{ p: 2, mb: 3 }}>
                                    <Typography variant="h6" gutterBottom>
                                        Add New Translation
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextField
                                            label="Key"
                                            fullWidth
                                            value={newKey}
                                            onChange={(e) => setNewKey(e.target.value)}
                                        />
                                        <TextField
                                            label="Value"
                                            fullWidth
                                            multiline
                                            rows={3}
                                            value={newValue}
                                            onChange={(e) => setNewValue(e.target.value)}
                                        />
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Button
                                                variant="contained"
                                                startIcon={<Save />}
                                                onClick={handleAddNew}
                                            >
                                                Add
                                            </Button>
                                            <Button
                                                variant="outlined"
                                                startIcon={<Cancel />}
                                                onClick={() => {
                                                    setAddingNew(false);
                                                    setNewKey('');
                                                    setNewValue('');
                                                }}
                                            >
                                                Cancel
                                            </Button>
                                        </Box>
                                    </Box>
                                </Paper>
                            ) : (
                                <Button
                                    variant="outlined"
                                    startIcon={<Add />}
                                    onClick={() => setAddingNew(true)}
                                    sx={{ mb: 3 }}
                                >
                                    Add New Translation
                                </Button>
                            )}

                            {/* Missing Keys Section - First */}
                            {missingKeys.length > 0 && (
                                <Paper sx={{ mb: 3 }}>
                                    <Box
                                        sx={{
                                            p: 2,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            cursor: 'pointer',
                                            bgcolor: '#fff3e0',
                                        }}
                                        onClick={() => setMissingExpanded(!missingExpanded)}
                                    >
                                        <Typography variant="h6">
                                            Missing Keys ({missingKeys.length})
                                        </Typography>
                                        <IconButton size="small">
                                            {missingExpanded ? <ExpandLess /> : <ExpandMore />}
                                        </IconButton>
                                    </Box>
                                    <Collapse in={missingExpanded}>
                                        <TableContainer>
                                            <Table>
                                                <TableHead>
                                                    <TableRow>
                                                        <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>Key</TableCell>
                                                        <TableCell sx={{ width: '60%', fontWeight: 'bold' }}>
                                                            English Value (Reference)
                                                        </TableCell>
                                                        <TableCell sx={{ width: '10%', fontWeight: 'bold' }}>Actions</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {missingKeys.map(key => (
                                                        <TableRow key={key} sx={{ bgcolor: '#fff3e0' }}>
                                                            <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                                {key}
                                                            </TableCell>
                                                            <TableCell>
                                                                {editingKey === key ? (
                                                                    <>
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{ display: 'block', mb: 1, color: 'text.secondary' }}
                                                                        >
                                                                            EN: {enLocaleData[key]}
                                                                        </Typography>
                                                                        <TextField
                                                                            fullWidth
                                                                            multiline
                                                                            rows={3}
                                                                            value={editValue}
                                                                            onChange={(e) => setEditValue(e.target.value)}
                                                                            autoFocus
                                                                            placeholder={`Enter ${language} translation`}
                                                                        />
                                                                    </>
                                                                ) : (
                                                                    <Typography sx={{ whiteSpace: 'pre-wrap', color: 'text.secondary' }}>
                                                                        {enLocaleData[key]}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {editingKey === key ? (
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => handleSave(key)}
                                                                        >
                                                                            <Save />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="secondary"
                                                                            onClick={handleCancel}
                                                                        >
                                                                            <Cancel />
                                                                        </IconButton>
                                                                    </Box>
                                                                ) : (
                                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => handleEdit(key, '')}
                                                                        >
                                                                            <Add />
                                                                        </IconButton>
                                                                        <IconButton
                                                                            size="small"
                                                                            color="primary"
                                                                            onClick={() => handleBulkEdit(key)}
                                                                            title="Bulk edit across languages"
                                                                            disabled={loadingBulkKey === key}
                                                                        >
                                                                            {loadingBulkKey === key ? (
                                                                                <CircularProgress size={16} />
                                                                            ) : (
                                                                                <EditNote />
                                                                            )}
                                                                        </IconButton>
                                                                    </Box>
                                                                )}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </Collapse>
                                </Paper>
                            )}

                            {/* Existing Translations Section - Collapsible */}
                            <Paper sx={{ mb: 3 }}>
                                <Box
                                    sx={{
                                        p: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => setExistingExpanded(!existingExpanded)}
                                >
                                    <Typography variant="h6">
                                        Existing Translations ({existingKeys.length})
                                    </Typography>
                                    <IconButton size="small">
                                        {existingExpanded ? <ExpandLess /> : <ExpandMore />}
                                    </IconButton>
                                </Box>
                                <Collapse in={existingExpanded}>
                                    <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ width: '30%', fontWeight: 'bold' }}>Key</TableCell>
                                            <TableCell sx={{ width: '60%', fontWeight: 'bold' }}>Value</TableCell>
                                            <TableCell sx={{ width: '10%', fontWeight: 'bold' }}>Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {existingKeys.map(key => (
                                            <TableRow key={key}>
                                                <TableCell sx={{ verticalAlign: 'top', wordBreak: 'break-word' }}>
                                                    {key}
                                                </TableCell>
                                                <TableCell>
                                                    {editingKey === key ? (
                                                        <TextField
                                                            fullWidth
                                                            multiline
                                                            rows={3}
                                                            value={editValue}
                                                            onChange={(e) => setEditValue(e.target.value)}
                                                            autoFocus
                                                        />
                                                    ) : (
                                                        <Typography sx={{ whiteSpace: 'pre-wrap' }}>
                                                            {localeData[key]}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    {editingKey === key ? (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleSave(key)}
                                                            >
                                                                <Save />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="secondary"
                                                                onClick={handleCancel}
                                                            >
                                                                <Cancel />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Box sx={{ display: 'flex', gap: 1 }}>
                                                            <IconButton
                                                                size="small"
                                                                onClick={() => handleEdit(key, localeData[key])}
                                                            >
                                                                <Edit />
                                                            </IconButton>
                                                            <IconButton
                                                                size="small"
                                                                color="primary"
                                                                onClick={() => handleBulkEdit(key)}
                                                                title="Bulk edit across languages"
                                                                disabled={loadingBulkKey === key}
                                                            >
                                                                {loadingBulkKey === key ? (
                                                                    <CircularProgress size={16} />
                                                                ) : (
                                                                    <EditNote />
                                                                )}
                                                            </IconButton>
                                                        </Box>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Collapse>
                    </Paper>

                            {/* Add New Button - Bottom (only show if at least one section is expanded) */}
                            {showBottomButton && (
                                addingNew ? (
                                    <Paper sx={{ p: 2, mb: 3 }}>
                                        <Typography variant="h6" gutterBottom>
                                            Add New Translation
                                        </Typography>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                            <TextField
                                                label="Key"
                                                fullWidth
                                                value={newKey}
                                                onChange={(e) => setNewKey(e.target.value)}
                                            />
                                            <TextField
                                                label="Value"
                                                fullWidth
                                                multiline
                                                rows={3}
                                                value={newValue}
                                                onChange={(e) => setNewValue(e.target.value)}
                                            />
                                            <Box sx={{ display: 'flex', gap: 2 }}>
                                                <Button
                                                    variant="contained"
                                                    startIcon={<Save />}
                                                    onClick={handleAddNew}
                                                >
                                                    Add
                                                </Button>
                                                <Button
                                                    variant="outlined"
                                                    startIcon={<Cancel />}
                                                    onClick={() => {
                                                        setAddingNew(false);
                                                        setNewKey('');
                                                        setNewValue('');
                                                    }}
                                                >
                                                    Cancel
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Paper>
                                ) : (
                                    <Button
                                        variant="outlined"
                                        startIcon={<Add />}
                                        onClick={() => setAddingNew(true)}
                                        sx={{ mb: 3 }}
                                    >
                                        Add New Translation
                                    </Button>
                                )
                            )}
                        </>
                    )}
                </Box>

                {/* Right Sidebar - Changes Preview */}
                <Box
                    sx={{
                        width: 400,
                        flexShrink: 0,
                        borderLeft: 1,
                        borderColor: 'divider',
                        p: 2,
                        overflow: 'auto',
                        bgcolor: 'background.paper',
                    }}
                >
                    <Typography variant="h6" gutterBottom>
                        Pending Changes
                    </Typography>
                    <Divider sx={{ mb: 2 }} />

                    {!hasChanges ? (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', mt: 4 }}>
                            No pending changes
                        </Typography>
                    ) : (
                        <>
                            <List sx={{ overflow: 'auto', flex: 1 }}>
                                {Object.keys(changes).map(changeKey => (
                                    <Box key={changeKey} sx={{ mb: 3 }}>
                                        <Typography variant="subtitle2" color="primary" gutterBottom>
                                            {changeKey}
                                        </Typography>
                                        {Object.keys(changes[changeKey]).map(key => (
                                            <ListItem
                                                key={key}
                                                sx={{
                                                    display: 'block',
                                                    bgcolor: 'grey.50',
                                                    mb: 1,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                                secondaryAction={
                                                    <IconButton
                                                        edge="end"
                                                        aria-label="delete"
                                                        size="small"
                                                        onClick={() => handleDeleteChange(changeKey, key)}
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText
                                                    primary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ fontWeight: 'bold', wordBreak: 'break-word', pr: 4 }}
                                                        >
                                                            {key}
                                                        </Typography>
                                                    }
                                                    secondary={
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ 
                                                                whiteSpace: 'pre-wrap', 
                                                                wordBreak: 'break-word',
                                                                fontSize: changes[changeKey][key] === '' ? '1.1em' : 'inherit',
                                                                fontStyle: changes[changeKey][key] === '' ? 'italic' : 'normal',
                                                                color: changes[changeKey][key] === '' ? 'text.secondary' : 'inherit'
                                                            }}
                                                        >
                                                            {changes[changeKey][key] === '' ? '<EMPTY>' : changes[changeKey][key]}
                                                        </Typography>
                                                    }
                                                />
                                            </ListItem>
                                        ))}
                                    </Box>
                                ))}
                            </List>

                            <Box sx={{ mt: 2 }}>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    startIcon={<Save />}
                                    onClick={handleSaveAll}
                                    disabled={loading}
                                >
                                    Save All Changes
                                </Button>
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                    {Object.keys(changes).reduce((acc, key) => acc + Object.keys(changes[key]).length, 0)} change(s)
                                </Typography>
                            </Box>
                        </>
                    )}
                </Box>
            </Box>

            {/* Commit Message Dialog */}
            <Dialog open={commitDialogOpen} onClose={handleCommitCancel} maxWidth="sm" fullWidth>
                <DialogTitle>Save Changes</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Please provide a commit message to describe your changes:
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        multiline
                        rows={3}
                        label="Commit Message"
                        placeholder="e.g., Updated French translations for assessment namespace"
                        value={commitMessage}
                        onChange={(e) => setCommitMessage(e.target.value)}
                        variant="outlined"
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCommitCancel} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCommitSave}
                        variant="contained"
                        color="primary"
                        disabled={!commitMessage.trim()}
                        startIcon={<Save />}
                    >
                        Save Changes
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Bulk Edit Dialog */}
            <Dialog open={bulkEditDialogOpen} onClose={handleBulkEditCancel} maxWidth="md" fullWidth>
                <DialogTitle>Bulk Edit: {bulkEditKey}</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Edit this translation key across all languages:
                    </Typography>
                    {loadingBulkData ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {languages.map(lang => (
                                <Grid item xs={12} sm={6} key={lang.code}>
                                    <TextField
                                        fullWidth
                                        label={`${lang.name} (${lang.code})`}
                                        multiline
                                        rows={2}
                                        value={bulkEditValues[lang.code] || ''}
                                        onChange={(e) => setBulkEditValues(prev => ({
                                            ...prev,
                                            [lang.code]: e.target.value
                                        }))}
                                        variant="outlined"
                                        size="small"
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleBulkEditCancel} color="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleFillWithAI}
                        variant="outlined"
                        color="primary"
                        startIcon={translatingAI ? <CircularProgress size={16} /> : <AutoAwesome />}
                        disabled={loadingBulkData || translatingAI || areAllFieldsEmpty()}
                        sx={{ mr: 'auto' }}
                    >
                        Fill with AI
                    </Button>
                    <Button
                        onClick={handleBulkEditApply}
                        variant="contained"
                        color="primary"
                        startIcon={<Save />}
                        disabled={loadingBulkData || translatingAI}
                    >
                        Apply Changes
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LanguagesEditor;

